<template>
  <div class="flex gap-1 my-[2px]">
    <div v-if="buy" class="w-1/2">
      <div class="flex">
        <div class="px-1 w-[25%] text-start me-1">
          <span
            class="text-c2 font-medium"
            :class="{
              'text-neutral-secondary-disable': isDisabled(buy.price),
            }"
          >
            {{ numberFormatter(buy.depthModels?.length ?? 0) }}
          </span>
        </div>

        <div class="flex items-center justify-between flex-1 relative">
          <div
            v-if="!isDisabled(buy.price)"
            class="absolute pointer-events-none left-0 top-0 bg-brand-primary darkTheme:bg-opacity-30 bg-opacity-20 h-6 rounded-s-sm transition-all ease-in-out duration-1000"
            :style="{
              width: `${progressPercentage(buy.volume)}%`,
            }"
          />

          <NPopper hover placement="top">
            <span
              v-gid="{
                module: 'order',
                screen: 'home',
                section: 'marketDepth',
                component: 'quantitySellButton',
                disabled: isDisabled(buy.price),
              }"
              class="text-c2 font-medium cursor-pointer"
              :class="{
                'pointer-events-none text-neutral-secondary-disable cursor-default':
                  isDisabled(buy.price),
              }"
              @click="
                $emit('volume-click', buy.price, ORDER_TYPES.buy, rowIndex)
              "
            >
              {{ numberFormatter(buy.volume) }}
            </span>

            <template #content>
              <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
                <span class="text-neutral-quaternary text-c2 font-medium">
                  {{
                    numberFormatter(totalVolumeCalculatorFn('buy', rowIndex))
                  }}
                </span>
              </div>
            </template>
          </NPopper>

          <NPopper hover placement="top">
            <span
              v-gid="{
                module: 'order',
                screen: 'home',
                section: 'marketDepth',
                component: 'priceSellButton',
                disabled: isDisabled(buy.price),
              }"
              class="text-c2 font-medium text-brand-primary"
              :class="
                isDisabled(buy.price)
                  ? 'text-neutral-secondary-disable'
                  : 'text-brand-primary cursor-pointer'
              "
              @click="$emit('price-click', buy.price, ORDER_TYPES.buy)"
            >
              {{ numberFormatter(buy.price) }}
            </span>

            <template #content>
              <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
                <NumberFormatter
                  #="{ number }"
                  :value="calcReferencePriceDifference(buy.price)"
                  maximum-fraction-digits="2"
                  minimum-fraction-digits="2"
                  number-style="percent"
                  sign-display="exceptZero"
                >
                  <NText
                    variant="caption2"
                    weight="medium"
                    class="text-neutral-quaternary"
                    dir="ltr"
                    :class="{
                      '!text-brand-secondary':
                        calcReferencePriceDifference(buy.price) > 0,
                      '!text-critical-secondary':
                        calcReferencePriceDifference(buy.price) < 0,
                    }"
                  >
                    {{ number }}
                  </NText>
                </NumberFormatter>
              </div>
            </template>
          </NPopper>
        </div>
      </div>
    </div>

    <div v-if="sell" class="w-1/2 ms-auto">
      <div class="flex">
        <div
          class="flex items-center justify-between flex-1 relative bg-transparent"
        >
          <div
            v-if="!isDisabled(sell.price)"
            class="absolute bg-critical-primary right-0 top-0 darkTheme:bg-opacity-30 bg-opacity-20 h-6 rounded-e-sm transition-all ease-in-out duration-1000"
            :style="{
              width: `${progressPercentage(sell.volume)}%`,
            }"
          />

          <NPopper hover placement="top">
            <span
              v-gid="{
                module: 'order',
                screen: 'home',
                section: 'marketDepth',
                component: 'priceBuyButton',
                disabled: isDisabled(sell.price),
              }"
              class="text-c2 font-medium text-brand-primary"
              :class="
                isDisabled(sell.price)
                  ? 'text-neutral-secondary-disable'
                  : 'cursor-pointer text-critical-primary'
              "
              @click="$emit('price-click', sell.price, ORDER_TYPES.sell)"
            >
              {{ numberFormatter(sell.price) }}
            </span>

            <template #content>
              <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
                <NumberFormatter
                  #="{ number }"
                  :value="calcReferencePriceDifference(sell.price)"
                  maximum-fraction-digits="2"
                  minimum-fraction-digits="2"
                  number-style="percent"
                  sign-display="exceptZero"
                >
                  <NText
                    variant="caption2"
                    weight="medium"
                    class="text-neutral-quaternary"
                    dir="ltr"
                    :class="{
                      '!text-brand-secondary':
                        calcReferencePriceDifference(sell.price) > 0,
                      '!text-critical-secondary':
                        calcReferencePriceDifference(sell.price) < 0,
                    }"
                  >
                    {{ number }}
                  </NText>
                </NumberFormatter>
              </div>
            </template>
          </NPopper>

          <NPopper hover placement="top">
            <span
              v-gid="{
                module: 'order',
                screen: 'home',
                section: 'marketDepth',
                component: 'quantityBuyButton',
                disabled: isDisabled(sell.price),
              }"
              class="text-c2 font-medium cursor-pointer"
              :class="{
                'pointer-events-none text-neutral-secondary-disable cursor-default':
                  isDisabled(sell.price),
              }"
              @click="
                $emit('volume-click', sell.price, ORDER_TYPES.sell, rowIndex)
              "
            >
              {{ numberFormatter(sell.volume) }}
            </span>

            <template #content>
              <div class="bg-neutral-quaternary px-2 py-1 rounded-md">
                <span class="text-neutral-quaternary text-c2 font-medium">
                  {{
                    numberFormatter(totalVolumeCalculatorFn('sell', rowIndex))
                  }}
                </span>
              </div>
            </template>
          </NPopper>
        </div>
        <div class="px-1 w-[25%] text-end ms-1">
          <span
            class="text-c2 font-medium"
            :class="{
              'text-neutral-secondary-disable': isDisabled(sell.price),
            }"
          >
            {{ numberFormatter(sell.depthModels?.length ?? 0) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ORDER_TYPES } from '@core/order/src/constants';
import { MarketDepthItems } from '@libs/api-reference/src/marketDepths';
import { numberFormatter } from '@libs/utils/src';
import NumberFormatter from '@/components/Number.vue';

interface Props {
  buy: MarketDepthItems;
  sell: MarketDepthItems;

  lowerPriceThreshold: number;
  upperPriceThreshold: number;
  maxVolume: number;

  rowIndex: number;
  totalVolumeCalculatorFn: (
    side: keyof typeof ORDER_TYPES,
    index: number
  ) => number;

  referencePrice: number;

  isOption?: boolean;
}

const props = defineProps<Props>();
defineEmits(['price-click', 'volume-click']);

const isDisabled = (price: number) => {
  return (
    !props.isOption &&
    (price < props.lowerPriceThreshold || price > props.upperPriceThreshold)
  );
};

const calcReferencePriceDifference = (price: number) => {
  return props.referencePrice
    ? (price - props.referencePrice) / props.referencePrice
    : 0;
};

const progressPercentage = (volume: number) => {
  return (volume / props.maxVolume) * 100;
};
</script>
